import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MuxPlayer from '@mux/mux-player-react';

import TimeDurationLine from '@/assets/icons/time_duration_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import settings from '@/config/settings';
import { formatBioscopeValue, parseLocale } from '@/utils/localeFormatters';
import { logger } from '@/utils/logger';

import type { VideoState } from '../../types/videoStream';

import { StopConnectionButton } from './StopConnectionButton';

import styles from './BehaviorVideo.module.scss';

interface StreamingViewProps {
    cageId: number;
    cageName?: string;
    videoState: VideoState;
    streamDuration: number;
    onStop: () => void;
}

export function StreamingView(props: StreamingViewProps) {
    const { cageId, cageName, videoState, streamDuration, onStop } = props;
    const { t, i18n } = useTranslation('behaviorModule');
    const locale = parseLocale(i18n.language);

    const [timeRemaining, setTimeRemaining] = useState<number>(streamDuration);

    // Derive countdown string from timeRemaining
    const videoCountDown = (() => {
        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    })();

    // Use ref to track if we've already triggered stop to prevent loops
    const hasStoppedRef = useRef(false);

    // Keep onStop in a ref to avoid re-running effects when the callback reference changes
    // This prevents infinite loops caused by unstable callback references from parent hooks
    const onStopRef = useRef(onStop);
    onStopRef.current = onStop;

    // DEBUG: Log renders and state changes to diagnose infinite loop in staging
    const renderCountRef = useRef(0);
    renderCountRef.current += 1;
    logger.dev('StreamingView', 'Render #', renderCountRef.current, {
        timeRemaining,
        hasStopped: hasStoppedRef.current,
        playbackId: videoState.playbackId,
        status: videoState.status,
    });

    // Initialize/reset state when component mounts or streamDuration changes (new stream starts)
    // This ensures proper state even if React reuses the component instance
    useEffect(() => {
        logger.dev('StreamingView', 'Reset effect - streamDuration:', streamDuration);
        hasStoppedRef.current = false;
        setTimeRemaining(streamDuration);
    }, [streamDuration]);

    // Handle auto-stop when time runs out
    // NOTE: We use onStopRef instead of onStop to avoid re-running this effect
    // when the parent's stopStream callback gets a new reference
    useEffect(() => {
        logger.dev(
            'StreamingView',
            'Auto-stop check - timeRemaining:',
            timeRemaining,
            'hasStopped:',
            hasStoppedRef.current
        );
        if (timeRemaining <= 0 && !hasStoppedRef.current) {
            logger.dev('StreamingView', 'Triggering auto-stop');
            hasStoppedRef.current = true;
            onStopRef.current();
        }
    }, [timeRemaining]); // Removed onStop from dependencies - using ref instead

    // Countdown timer - runs once on mount, uses functional update to avoid stale closure
    useEffect(() => {
        logger.dev('StreamingView', 'Starting countdown interval');
        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    logger.dev('StreamingView', 'Countdown reached 0');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            logger.dev('StreamingView', 'Cleaning up countdown interval');
            clearInterval(interval);
        };
    }, []);

    return (
        <div className={styles.videoContainer}>
            <MuxPlayer
                playbackId={videoState.playbackId}
                accentColor={settings.primaryColor}
                autoPlay={true}
                metadata={{
                    video_id: `behavior-video-${cageId}`,
                    video_title: cageName,
                    viewer_user_id: videoState.userId,
                }}
            />
            <div className={styles.videoOverlay}>
                <div className={styles.overlayLeft}>
                    <div className={styles.cageName}>{cageName}</div>
                    {videoCountDown && (
                        <div className={styles.timer}>
                            <SvgIcon icon={TimeDurationLine} size="custom" customSize={20} />
                            <span>{videoCountDown}</span>
                        </div>
                    )}
                </div>
                <div className={styles.overlayRight}>
                    {videoState.bioscopeData && (
                        <div className={styles.bioscopeData}>
                            {videoState.bioscopeData.depth !== undefined && (
                                <div>
                                    {t('videoStream.depth', 'Depth')}:{' '}
                                    {formatBioscopeValue(
                                        videoState.bioscopeData.depth,
                                        'depth',
                                        locale
                                    )}
                                </div>
                            )}
                            {videoState.bioscopeData.pitch !== undefined && (
                                <div>
                                    {t('videoStream.pitch', 'Pitch')}:{' '}
                                    {formatBioscopeValue(
                                        videoState.bioscopeData.pitch,
                                        'pitch',
                                        locale
                                    )}
                                </div>
                            )}
                            {videoState.bioscopeData.roll !== undefined && (
                                <div>
                                    {t('videoStream.roll', 'Roll')}:{' '}
                                    {formatBioscopeValue(
                                        videoState.bioscopeData.roll,
                                        'roll',
                                        locale
                                    )}
                                </div>
                            )}
                            {videoState.bioscopeData.waterTemperature !== undefined && (
                                <div>
                                    {t('videoStream.temp', 'Temp')}:{' '}
                                    {formatBioscopeValue(
                                        videoState.bioscopeData.waterTemperature,
                                        'waterTemperature',
                                        locale
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <StopConnectionButton onStop={onStop} />
        </div>
    );
}
